import { usePmeTarifacaoPage } from '@/features/proposal-analyses/pme/use-pme-tarifacao.hook';
import { groupLifeService } from '@/services/group-life';
import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, DataTable, Text } from '@noorden-insurtech/alba-design-system';
import { Download, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PmePolicyLifeDetailModal } from './pme-policy-life-detail.modal';
import { createPmePolicyLivesColumns } from './pme-policy-lives-columns';

const DEFAULT_PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 300;
/**
 * As vidas de uma apólice EMITIDA não mudam, então cada página já visitada
 * serve do cache — sem isso, ir para a página 3 e voltar para a 2 refazia a
 * chamada ao BFF (que varre o índice do InsureMO).
 */
const LIVES_STALE_TIME_MS = 5 * 60 * 1000;

type Props = {
  policyId: string;
  /** `__STIPULATOR__` (master) ou o id do subestipulante. */
  targetId: string;
  /** Razão social da entidade — compõe o nome do arquivo exportado. */
  exportLabel: string;
};

/**
 * "Lista de Vidas" de UM estipulante da apólice PME.
 *
 * Busca e paginação são server-side (`GET .../proposals/:policyId/pricing`),
 * filtradas pelo `targetId` — cada card de estipulante tem a sua instância, com
 * o seu estado de página e de busca.
 */
export function PmePolicyLivesTable({
  policyId,
  targetId,
  exportLabel,
}: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [detailLife, setDetailLife] = useState<GroupLifeTarifacaoLife | null>(
    null
  );
  const [isExporting, setIsExporting] = useState(false);

  // Debounce: cada tecla dispararia uma chamada ao BFF, que por sua vez varre o
  // índice do InsureMO. O `early return` evita o reset de página na MONTAGEM
  // (termo vazio = termo atual), que jogava a grade de volta para a página 1.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return;
    const timer = setTimeout(() => {
      setSearch(next);
      setPageNo(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const pageQuery = usePmeTarifacaoPage(
    policyId,
    'proposal',
    pageNo,
    search || undefined,
    pageSize,
    targetId,
    { staleTime: LIVES_STALE_TIME_MS }
  );

  const lives = pageQuery.data?.data ?? [];
  const meta = pageQuery.data?.meta;

  const columns = useMemo(
    () => createPmePolicyLivesColumns({ onExpand: setDetailLife }),
    []
  );

  async function handleExport() {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const result = await groupLifeService.downloadMembersExport({
        quoteId: policyId,
        targetId,
        filename: exportLabel,
      });
      if (!result.success) {
        toaster.create({
          type: 'error',
          title: 'Exportar vidas',
          description: result.error ?? 'Não foi possível exportar as vidas.',
        });
        return;
      }
      toaster.create({
        type: 'success',
        title: 'Exportação concluída',
        description: 'A planilha de vidas foi gerada com sucesso.',
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Exportar vidas',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível exportar as vidas.',
      });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>Lista de Vidas</Text>

      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div className='flex flex-col gap-2'>
          <Text className='text-sm font-bold text-alba-black-500'>Buscar</Text>
          <div className='flex w-full items-center gap-2 rounded-full border border-alba-gray-300 px-4 py-2 lg:w-72'>
            <input
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder='Digite'
              className='w-full bg-transparent text-sm text-alba-black-500 outline-none'
            />
            <Search className='h-4 w-4 shrink-0 text-alba-gray-600' />
          </div>
        </div>

        <Button
          variant='secondary'
          size='md'
          className='flex items-center gap-2 self-start lg:self-auto'
          loading={isExporting}
          disabled={isExporting}
          onClick={() => void handleExport()}
        >
          <Download className='h-4 w-4' />
          Exportar
        </Button>
      </div>

      {pageQuery.isError ? (
        <Text className='text-sm text-alba-error-red-500'>
          Não foi possível carregar as vidas desta apólice.
        </Text>
      ) : (
        <DataTable
          columns={columns}
          data={lives}
          loading={pageQuery.isLoading || pageQuery.isFetching}
          pageSize={pageSize}
          pageSizeOptions={[6, 12, 24, 48]}
          totalItems={meta?.total ?? 0}
          totalPages={meta?.totalPages ?? 0}
          enableSorting={false}
          emptyMessage='Nenhuma vida encontrada.'
          onPageChange={(page, size) => {
            if (size !== pageSize) {
              setPageSize(size);
              setPageNo(1);
              return;
            }
            setPageNo(page);
          }}
        />
      )}

      <PmePolicyLifeDetailModal
        isOpen={Boolean(detailLife)}
        life={detailLife}
        onClose={() => setDetailLife(null)}
      />
    </div>
  );
}
